import { faPenToSquare } from "@fortawesome/pro-duotone-svg-icons";
import { faAngleDoubleLeft, faAngleDoubleRight, faAngleLeft, faAngleRight, faMagnifyingGlass, faPlus } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AutoComplete, Button, Card, message, Select, Skeleton, Spin, Table } from "antd";
import { Option } from "antd/es/mentions";
import { useEffect, useState } from "react";
import ReactCountryFlag from "react-country-flag";
import { useNavigate } from "react-router-dom";
import AddEditFormComponent from "../components/AddEditFormComponent";
import ImportDrawer from "../components/ImportDrawer";
import OrgService from "../services/Org/OrgService";
import AppButton from "../common/AppButton";

function SupplierPage() {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("");
  const [dataSource, setDataSource] = useState({ total: 0, data: [] });
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [openImport, setOpenImport] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [list, setList] = useState({
    request: {
      name: null,
      email: null,
      phoneCode: null,
      phoneNumber: null,
      orgType: null,
      fax: null,
      paymentTerms: null,
      isActive: true,
      supplierId: null,
      pageFilter: { all: false, size: 50, num: 0 },
      sortFilter: { field: "", order: "" },
    },
    response: { total: 0, data: [] },
  });
  const [searchInput, setSearchInput] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedSupplierId, setSelectedSupplierId] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    fetchSuppliers(list.request);
  }, []);

  const handleAddSupplier = (values) => {
    if (type === "edit") {
      setDataSource((prevData) => ({
        ...prevData,
        data: prevData.data.map((item) =>
          item.key === editingSupplier.key ? { ...item, ...values } : item
        ),
      }));
    } else {
      const newSupplier = { key: Date.now(), ...values };
      setDataSource((prevDataSource) => ({
        ...prevDataSource,
        data: [...prevDataSource.data, newSupplier],
        total: prevDataSource.total + 1,
      }));
    }
    setOpen(false);
  };

  const handleEdit = (record) => {
    setEditingSupplier(record);
    setType("edit");
    setOpen(true);
  };

  const handleApplyFilters = (values) => {
    const updatedRequest = {
      ...list.request,
      name: values.name || null,
      email: values.email || null,
      phoneCode: values.phoneCode || null,
      phoneNumber: values.phoneNumber || null,
      fax: values.fax || null,
      paymentTerms: values.paymentTerms || null,
      isActive: values.isActive || null,
      supplierId: null,
    };
    setSelectedSupplierId(null);
    setList((prevState) => ({
      ...prevState,
      request: updatedRequest,
    }));
    fetchSuppliers(updatedRequest);
  };

  const exportToExcel = (request) => {
    OrgService.exportToExcel(request)
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        const contentDisposition = response.headers["content-disposition"];
        const filename =
          contentDisposition?.match(/filename="(.+)"/)?.[1] || "suppliers.xlsx";
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        message.success("Excel file downloaded successfully.");
      })
      .catch((error) => {
        console.error("Error exporting to Excel:", error);
        message.error("Failed to download Excel file.");
      });
  };

  const handleTableChange = (pagination, filters, sorter) => {
    let isActive = null;

    if (filters.isActive?.length) {
      const val = filters.isActive[0];
      if (val === "true") isActive = true;
      if (val === "false") isActive = false;
    }

    const updatedRequest = {
      ...list.request,
      isActive,
      sortFilter: {
        field: sorter.field || "",
        order:
          sorter.order === "ascend"
            ? "asc"
            : sorter.order === "descend"
              ? "desc"
              : "",
      },
      pageFilter: {
        ...list.request.pageFilter,
        size: pagination.pageSize || list.request.pageFilter.size,
        num: (pagination.current ?? list.request.pageFilter.num + 1) - 1,
      },
    };
    setList((prevState) => ({
      ...prevState,
      request: updatedRequest,
    }));
    fetchSuppliers(updatedRequest);
  };

  const handleView = (record) => {
    navigate("/settings/supplier/supplier-details", {
      state: { supplierID: record.id },
    });
  };

  const handleStatusChange = async (newStatus, id) => {
    const record = dataSource.data.find((item) => item.id === id);
    if (record) {
      const request = { ...record, isActive: newStatus };
      await OrgService.updateOrgSupplier(request);
      message.success(
        `Status has been updated to ${newStatus ? "Active" : "Inactive"}`
      );
      fetchSuppliers(list.request);
    }
  };

  const fetchSuppliers = (request) => {
    setIsLoading(true);
    OrgService.getAllSupplier(request)
      .then((response) => {
        if (response.status === 200) {
          setDataSource(response.data);
          setList((prevState) => ({
            ...prevState,
            response: response.data,
          }));
          setIsLoading(false);
        }
      })
      .catch((error) => {
        console.error("Error fetching suppliers:", error);
        message.error("Failed to fetch suppliers.");
      });
  };

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const getRowSelection = () => ({
    selectedRowKeys,
    onChange: onSelectChange,
  });

  const handleSearchInputChange = (value) => {
    if (value) {
      setSearchInput(value);
      if (value !== searchInput) {
        setSearchLoading(true);
        OrgService.getAllSupplier({
          name: value,
        })
          .then((response) => {
            if (response.status === 200) {
              setSearchResults(response.data.data || response.data);
            }
          })
          .catch((err) => {
            console.error("Search Failed", err);
            setSearchResults([]);
            message.error("Search failed. Please try again.");
          })
          .finally(() => {
            setSearchLoading(false);
          });
      } else {
        setSearchResults([]);
      }
    } else {
      setSearchInput("");
      setSearchResults([]);
    }
  };

  const handleSupplierSelect = (selectedSupplier) => {
    setSearchInput("");
    setSearchResults([]);
    const { name } = selectedSupplier;
    const updatedRequest = {
      ...list.request,
      name: name,
      pageFilter: { ...list.request.pageFilter, num: 0 },
    };
    setList((prevState) => ({
      ...prevState,
      request: updatedRequest,
    }));
    fetchSuppliers(updatedRequest);
    setSearchInput(name);
  };

  const handleClearSearchResult = () => {
    setSelectedSupplierId(null);
    setSearchInput("");
    setSearchResults([]);
    const updatedRequest = {
      ...list.request,
      supplierId: null,
      name: null,
      pageFilter: { ...list.request.pageFilter, num: 0 },
    };
    setList((prevState) => ({
      ...prevState,
      request: updatedRequest,
    }));
    fetchSuppliers(updatedRequest);
  };

  const columns = [
    {
      title: "",
      key: "action",
      align: "center",
      width: 100,
      render: (_, record) => (
        <Button
          type="link"
          className="group"
          onClick={() =>
            navigate("/settings/supplier/details", {
              state: {
                mode: "edit",
                supplier: record,
              },
            })
          }
        >
          <FontAwesomeIcon
            icon={faPenToSquare}
            className="text-xl text-black p-2 hover:text-cyan-500"
          />
        </Button>
      ),
    },
    {
      title: "Supplier Name",
      dataIndex: "name",
      key: "name",
      align: "left",
      sorter: true,
      width: 400,
      render: (text) => (isLoading ? <Skeleton active size="large" /> : text),
    },
    {
      title: "Short Code",
      dataIndex: "supplierShortCode",
      key: "supplierShortCode",
      align: "left",
      width: 125,
      sorter: true,
      render: (text) => (isLoading ? <Skeleton active size="small" /> : text),
    },
    {
      title: "Country",
      key: "country",
      align: "center",
      width: 70,
      sorter: false,
      render: (text, record) =>
        isLoading ? (
          <Skeleton active size="small" />
        ) : (
          <ReactCountryFlag countryCode={record.address.countryCd} svg className="!w-6 !h-6" />
        ),
    },
    {
      title: "State",
      width: 150,
      key: "state",
      align: "left",
      sorter: true,
      render: (text, record) =>
        isLoading ? <Skeleton active size="large" /> : record.address.state,
    },
    {
      title: "City",
      width: 150,
      key: "City",
      align: "left",
      sorter: true,
      render: (text, record) =>
        isLoading ? <Skeleton active size="large" /> : record.address.city,
    },
    {
      title: "Active",
      width: 70,
      key: "isActive",
      align: "center",
      sorter: false,
      filterMultiple: false,
      filters: [
        { text: "Active", value: "true" },
        { text: "Inactive", value: "false" },
        { text: "All", value: "all" },
      ],
      filteredValue:
        list.request.isActive === null
          ? ["all"]
          : [String(list.request.isActive)],
      render: (_, record) => (
        <div className="flex items-center justify-center w-[80px]">
          <span
            className={`w-4 h-4 opacity-60 rounded-full ${record.isActive ? "bg-green-500" : "bg-red-500"
              }`}
          />
        </div>
      ),
    },
  ];

  const renderPagination = () => {
    const current = (list.request?.pageFilter?.num ?? 0) + 1;
    const pageSize = list.request?.pageFilter?.size ?? 20;
    const total = dataSource?.total ?? 0;
    const totalPages = Math.ceil(total / pageSize);

    const currentSorter = {
      field: list.request.sortFilter.field,
      order:
        list.request.sortFilter.order === "asc"
          ? "ascend"
          : list.request.sortFilter.order === "desc"
            ? "descend"
            : "",
    };

    const onCustomPageChange = (page, size) => {
      handleTableChange({ current: page, pageSize: size }, {}, currentSorter);
    };

    const onPageSizeChange = (size) => {
      onCustomPageChange(1, size); // Reset to first page when size changes
    };

    return (
      <div className="flex justify-between items-center mt-2 px-2">
        {/* Rows per page selector + info */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Rows per page:</span>
          <Select
            value={String(pageSize || 20)}
            onChange={(val) => onPageSizeChange(Number(val))}
            size="medium"
            style={{ width: 80 }}
          >
            <Option value="10">10</Option>
            <Option value="20">20</Option>
            <Option value="50">50</Option>
            <Option value="100">100</Option>
          </Select>
        </div>

        {/* Custom buttons */}
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-600">
            {`${(current - 1) * pageSize + 1}-${Math.min(
              current * pageSize,
              total
            )} of ${total} items`}
          </span>
          <div className="flex gap-2">
            <Button
              size="medium"
              onClick={() => onCustomPageChange(1, pageSize)}
              disabled={current === 1}
            >
              <FontAwesomeIcon icon={faAngleDoubleLeft} />
            </Button>
            <Button
              size="medium"
              onClick={() => onCustomPageChange(current - 1, pageSize)}
              disabled={current === 1}
            >
              <FontAwesomeIcon icon={faAngleLeft} />
            </Button>
            <Button
              size="medium"
              onClick={() => onCustomPageChange(current + 1, pageSize)}
              disabled={current === totalPages}
            >
              <FontAwesomeIcon icon={faAngleRight} />
            </Button>
            <Button
              size="medium"
              onClick={() => onCustomPageChange(totalPages, pageSize)}
              disabled={current === totalPages}
            >
              <FontAwesomeIcon icon={faAngleDoubleRight} />
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="px-8 mb-4">
      <div className="flex justify-between items-end w-full my-2 px-2">
        <span
          className="!font-medium text-[30px] !text-[#015373] !m-0 pl-4 mt-10 font-inter"
        >
          Supplier
        </span>
        <div className="flex justify-around gap-2 mr-8">
          <AutoComplete
            className="border w-72 font-inter !bg-gray-300"
            placeholder="Search supplier"
            prefix={
              <FontAwesomeIcon
                icon={faMagnifyingGlass}
                className="opacity-70 mr-2"
              />
            }
            options={searchResults.map((item) => ({
              value: item.id,
              label: (
                <div className="p-2">
                  {item.name} - {item.email || "N/A"}
                </div>
              ),
              item,
            }))}
            value={searchInput}
            onChange={handleSearchInputChange}
            onSelect={(value, option) => {
              handleSupplierSelect(option.item);
            }}
            allowClear
            onClear={handleClearSearchResult}
            notFoundContent={
              searchLoading ? <Spin size="large" /> : "No results found"
            }
          />
          <AppButton variant="new" onClick={() =>
              navigate("/settings/supplier/details", {
                state: {
                  mode: "add",
                },
              })
            }>
              New
          </AppButton>
        </div>
      </div>
      <Card className="rounded-3xl shadow-xl bg-white bg-opacity-80">
        <Table
          className="mx-0 mt-4 border-2 border-gray-300 rounded-lg overflow-x-auto"
          columns={columns}
          showSorterTooltip={false}
          dataSource={
            isLoading
              ? new Array(10).fill(null).map((_, index) => ({ key: index }))
              : dataSource?.data?.map((item, index) => ({
                ...item,
                key: index,
              }))
          }
          rowClassName={(_, index) =>
            index % 2 === 0 ? "text-sm" : "text-sm bg-[#f3f3f3]"
          }
          pagination={false} // Disable default Ant Design pagination
          onChange={handleTableChange}
          bordered={false}
          scroll={{ x: "max-content", y: "calc(100vh - 270px)" }}
          size="small"
        />
        {renderPagination()} {/* Add custom pagination */}
        <AddEditFormComponent
          open={open}
          onClose={() => setOpen(false)}
          onSubmit={handleAddSupplier}
          initialValues={editingSupplier}
          type={type}
          formType="supplier"
          onSuccess={() => {
            const { request } = list;
            fetchSuppliers(request);
          }}
        />
        <ImportDrawer
          open={openImport}
          onClose={() => setOpenImport(false)}
          type={"SupplierImport"}
        />
      </Card>
    </div>
  );
}

export default SupplierPage;
